import React, { useEffect, useState, useCallback } from 'react'
import { DocumentCheckIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { INTERAX_COLORS } from '../../../constants/colors'
import { NotificationCard } from './NotificationCard'

interface FeedbackReadyNotificationProps {
  sessionId: string
  onDismiss: () => void
  onNavigate: () => void
  autoDismissAfter?: number
}

/**
 * In-app notification when session feedback is ready (gamer).
 */
export const FeedbackReadyNotification: React.FC<FeedbackReadyNotificationProps> = ({
  sessionId: _sessionId,
  onDismiss,
  onNavigate,
  autoDismissAfter = 10000
}) => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (autoDismissAfter > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false)
        setTimeout(onDismiss, 300)
      }, autoDismissAfter)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [autoDismissAfter, onDismiss])

  const handleLinkClick = useCallback(() => {
    onNavigate()
  }, [onNavigate])

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsVisible(false)
      setTimeout(onDismiss, 300)
    },
    [onDismiss]
  )

  if (!isVisible) return null

  return (
    <NotificationCard
      to={GamerRoutes.LATEST_SESSION_FEEDBACK}
      icon={<DocumentCheckIcon style={{ width: '18px', height: '18px', color: INTERAX_COLORS.sun }} aria-hidden />}
      title={t('gamer.notifications.feedbackReady', 'Your feedback is ready')}
      subtitle={t('gamer.notifications.viewFeedback', 'View feedback')}
      onLinkClick={handleLinkClick}
      onCloseClick={handleDismiss}
      isVisible={isVisible}
      closeButtonAriaLabel={t('common.cancel', 'Cancel')}
    />
  )
}
